function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { titleValidation } from 'validators';
import { Autocomplete, Callout, TableList, TextField, Typography, useGlobalContext } from 'seagull';
import { useStore, useStoreActions } from "../../contexts";
import { relationshipModel, serviceTemplateModel, timeModel } from "../../models";
import { useOnMount } from "../../hooks/useOnMount";
import { useServiceTemplateApi } from "../../hooks/useServiceTemplateApi";
import { getUnitName } from "../../utils/unit-name.utils";
import { handleRequest, sortAlphabetically } from "../../infra/utils";
import { connectControllerToTextField, genericConnector } from "../../utils/react-hook-form.utils";
import { BillMode } from "../ManualBillModal/ManualBillModal.utils";
import ManualBillModal from "../ManualBillModal/ManualBillModal";
import ManualBillModalSelectionField from "../ManualBillModal/ManualBillModalSelectionField";
import DiscountTextField from "../DiscountTextField";
import { getOptionLabel, getOptionSelected, validateGreaterThan0, getSuccessToastText, getTableInfo, getTotal, getClientOptions, getServiceTemplateOptions, getClientApprovalCalloutText, initialDiscount, discountToDTO } from "./AddChargeModal.utils";
import styles from "./AddChargeModal.module.scss";
import { utils } from "../../utils";
import { getCreditsWarning, makeInvoiceDateInfo } from "../../business-logic/billingLogic";
const addChargeLoader = 'addCharge';
export default function AddChargeModal(props) {
  const {
    context,
    dismissModal,
    onRefresh
  } = props;
  const inRelationshipContext = !!context;
  const {
    data: agreements,
    loading: loadingAgreements,
    error: agreementsError
  } = useStore(state => state.agreements);
  const {
    fetchAgreements,
    logBI,
    api
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const serviceTemplateApi = useServiceTemplateApi();
  const [loadingServiceTemplates, setLoadingServiceTemplates] = useState(true);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [agreementOptions, setAgreementOptions] = useState([]);
  const [selectedServiceOption, setSelectedServiceOption] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceTemplatesError, setServiceTemplatesError] = useState();
  const [actualInvoiceDateInfo, setActualInvoiceDateInfo] = useState(context === null || context === void 0 ? void 0 : context.invoiceDateInfo);
  const [discount, setDiscount] = useState(initialDiscount);
  const [invoiceDateInfoError, setInvoiceDateInfoError] = useState();
  const {
    columnDefs,
    headerItems
  } = useMemo(() => getTableInfo(discount), [discount]);
  const dataError = agreementsError || serviceTemplatesError || invoiceDateInfoError;
  const form = useForm({
    mode: 'all',
    defaultValues: {
      selectedClient: null,
      selectedAgreement: null,
      service: '',
      quantity: '',
      rate: '',
      note: ''
    }
  });
  const {
    control,
    setValue,
    watch,
    resetField,
    handleSubmit
  } = form;
  const [selectedClient, selectedAgreement, quantity, rate] = watch(['selectedClient', 'selectedAgreement', 'quantity', 'rate']);
  const disableFields = !inRelationshipContext && !selectedAgreement;
  const total = useMemo(() => {
    return getTotal(quantity, rate, selectedServiceOption, discount);
  }, [quantity, rate, selectedServiceOption, discount]);
  const clientOptions = useMemo(() => {
    return getClientOptions(inRelationshipContext, agreements);
  }, [inRelationshipContext, agreements]);
  const serviceTemplateOptions = useMemo(() => {
    return getServiceTemplateOptions(serviceTemplates, loadingServiceTemplates);
  }, [serviceTemplates, loadingServiceTemplates]);
  const actualRelationship = useMemo(() => {
    return context ? context.relationship : agreements && selectedAgreement ? agreements.find(a => a.id === selectedAgreement.value) : undefined;
  }, [agreements, context, selectedAgreement]);
  useOnMount(() => {
    if (!context) {
      fetchAgreements();
    }
    serviceTemplateApi.get({
      includeAdhocCharges: true,
      includeDeleted: false
    }).then(services => {
      setServiceTemplates(services.raw);
      setLoadingServiceTemplates(false);
    }).catch(err => setServiceTemplatesError(err));
  });
  useEffect(() => {
    async function fetchInvoiceDateInfo(relationshipId) {
      try {
        const dates = await api.invoiceDraft.getDates(relationshipId);
        if (!dates.length) {
          setActualInvoiceDateInfo(undefined);
        } else {
          const invoiceDraft = await api.invoiceDraft.getByDate(relationshipId, dates[0]);
          const newDateInfo = invoiceDraft ? makeInvoiceDateInfo(dates, invoiceDraft.issueDate) : undefined;
          setActualInvoiceDateInfo(newDateInfo);
        }
      } catch (err) {
        setInvoiceDateInfoError(err);
      }
    }
    if (!(context !== null && context !== void 0 && context.invoiceDateInfo)) {
      if (actualRelationship != null) {
        fetchInvoiceDateInfo(actualRelationship.id);
      } else {
        setActualInvoiceDateInfo(undefined);
      }
    }
  }, [actualRelationship, context]);
  useEffect(() => {
    // The error in rate field depends on total amount so we trigger change detection when total changes.
    form.trigger(['rate']);
  }, [total]);
  async function _onSubmit(data) {
    const {
      mode
    } = data;
    globalContext.startLoader(addChargeLoader);
    const {
      success
    } = await handleRequest(sendAddChargeRequest(data));
    if (!success) {
      globalContext.showErrorToast('Failed to add charge', "add-charge-".concat(mode, "-error-toast"));
    } else {
      onRefresh && (await onRefresh());
      dismissModal();
      globalContext.showSuccessToast(getSuccessToastText(mode), "add-charge-".concat(mode, "-success-toast"));
    }
    globalContext.stopLoader(addChargeLoader);
  }
  async function sendAddChargeRequest(data) {
    if (!actualRelationship) {
      throw new Error('no actualRelationship');
    }
    const {
      mode,
      issueDate,
      note
    } = data;
    const biPayload = {
      title: selectedServiceOption.label,
      note,
      price: total === null || total === void 0 ? void 0 : total.toDP(2).toNumber()
    };
    const addChargeBody = {
      name: selectedServiceOption.label,
      chargeOn: timeModel.toDatestampArray(issueDate),
      serviceTemplateId: selectedService === null || selectedService === void 0 ? void 0 : selectedService.id,
      note,
      rate: utils.dollarsToCents(+rate),
      quantity: utils.dollarsToCents(+quantity),
      discount: discountToDTO(discount)
    };
    switch (mode) {
      case BillMode.Upcoming:
        {
          logBI(10571, biPayload);
          await api.charge.create(actualRelationship.id, addChargeBody);
          return;
        }
      case BillMode.Now:
        {
          logBI(10579, biPayload);
          const chargeId = await api.charge.create(actualRelationship.id, addChargeBody);
          return api.charge.billNow(actualRelationship.id, chargeId, addChargeBody);
        }
      case BillMode.FutureDate:
        {
          logBI(10583, biPayload);
          await api.charge.create(actualRelationship.id, addChargeBody);
          return;
        }
    }
  }
  function applyServiceOptionChange(newValue, fallbackServiceOptionValue) {
    setSelectedServiceOption(newValue || fallbackServiceOptionValue || null);
    const serviceId = newValue === null || newValue === void 0 ? void 0 : newValue.value;
    const service = serviceId ? serviceTemplates.find(s => s.id === serviceId) : undefined;
    setSelectedService(service || null);
    if (service) {
      setValue('quantity', serviceTemplateModel.isFixedPrice(service.cost) ? '1' : '', {
        shouldValidate: true
      });
      setValue('rate', serviceTemplateModel.price(service).abs().toString(), {
        shouldValidate: true
      });
      const discount = serviceTemplateModel.discount(service);
      setDiscount(discount ? {
        type: discount.type(),
        amount: discount.amount().toString()
      } : initialDiscount);
    } else {
      resetField('quantity');
      resetField('rate');
      setDiscount(initialDiscount);
    }
  }
  function clearSelectedService() {
    // setTimeout is necessary if service-input is focused, to assure its blurred before resetting the field.
    setTimeout(() => {
      applyServiceOptionChange(null);
      resetField('service');
    });
  }
  if (dataError) {
    throw dataError;
  }
  return /*#__PURE__*/React.createElement(ManualBillModal, {
    title: "Add Charge",
    invoiceDateInfo: actualInvoiceDateInfo,
    effectiveDate: actualRelationship === null || actualRelationship === void 0 ? void 0 : actualRelationship.effectiveDate,
    disableFields: disableFields,
    form: form,
    onCancel: () => dismissModal(),
    onSubmit: data => handleSubmit(() => _onSubmit(data))(),
    agreementPickerContent: _ref => {
      let {
        changeMode
      } = _ref;
      return context ? null : /*#__PURE__*/React.createElement("div", {
        className: styles.selectors
      }, /*#__PURE__*/React.createElement(ManualBillModalSelectionField, null, /*#__PURE__*/React.createElement(Controller, {
        control: control,
        name: "selectedClient",
        rules: {
          required: true
        },
        render: renderProps => /*#__PURE__*/React.createElement(Autocomplete, {
          selectOnFocus: true,
          fullWidth: true,
          options: clientOptions,
          loading: loadingAgreements,
          "data-testid": "add-charge-client-selector",
          textFieldProps: {
            label: 'Client',
            inputRef: renderProps.field.ref,
            error: genericConnector(renderProps).showErrors
          },
          value: renderProps.field.value,
          getOptionLabel: getOptionLabel,
          getOptionSelected: getOptionSelected,
          onBlur: renderProps.field.onBlur,
          onChange: (event, value) => {
            const newSelectedClient = value ? value : null;
            if (newSelectedClient === selectedClient) {
              return;
            }
            renderProps.field.onChange(newSelectedClient);
            changeMode(BillMode.Upcoming);
            if (!newSelectedClient) {
              setAgreementOptions([]);
              setValue('selectedAgreement', null);
              clearSelectedService();
              return;
            }
            const newRelationshipOptions = (agreements || []).filter(a => newSelectedClient.value === a.customer.id && relationshipModel.isApproved(a) && !a.isSample).map(a => ({
              value: a.id,
              label: a.title || "unnamed (from ".concat(timeModel.formatDate(a.effectiveDate), ")")
            }));
            setAgreementOptions(sortAlphabetically(newRelationshipOptions, 'label', {
              ascending: true,
              ignoreCase: true
            }));
            setValue('selectedAgreement', newRelationshipOptions.length === 1 ? newRelationshipOptions[0] : null);
          }
        })
      })), /*#__PURE__*/React.createElement(ManualBillModalSelectionField, null, /*#__PURE__*/React.createElement(Controller, {
        control: control,
        name: "selectedAgreement",
        rules: {
          required: true
        },
        render: renderProps => /*#__PURE__*/React.createElement(Autocomplete, {
          selectOnFocus: true,
          fullWidth: true,
          disabled: !selectedClient,
          "data-testid": "add-charge-agreement-selector",
          textFieldProps: {
            label: 'Agreement',
            inputRef: renderProps.field.ref,
            error: genericConnector(renderProps).showErrors
          },
          value: renderProps.field.value,
          options: agreementOptions,
          getOptionLabel: getOptionLabel,
          getOptionSelected: getOptionSelected,
          onChange: (event, value) => {
            const newSelectedAgreement = value ? value : null;
            if (newSelectedAgreement === selectedAgreement) {
              return;
            }
            renderProps.field.onChange(newSelectedAgreement);
            changeMode(BillMode.Upcoming);
            if (!newSelectedAgreement) {
              clearSelectedService();
            }
          }
        })
      })));
    },
    infoBoxContent: _ref2 => {
      let {
        mode,
        issueDate
      } = _ref2;
      const creditsWarning = getCreditsWarning(mode, context === null || context === void 0 ? void 0 : context.totalCredits);
      return /*#__PURE__*/React.createElement("div", {
        className: styles.callouts
      }, /*#__PURE__*/React.createElement(Callout, null, getClientApprovalCalloutText(issueDate, actualInvoiceDateInfo)), creditsWarning && /*#__PURE__*/React.createElement(Callout, {
        "data-testid": "has-credits-callout"
      }, creditsWarning.text));
    },
    tableContent: () => /*#__PURE__*/React.createElement(TableList.Provider, {
      columnDefs: columnDefs
    }, /*#__PURE__*/React.createElement(TableList.Header, {
      items: headerItems,
      "data-testid": "add-charge-table-header"
    }), /*#__PURE__*/React.createElement(TableList.Row, {
      disableShadow: true,
      items: [{
        value: /*#__PURE__*/React.createElement(Controller, {
          control: control,
          name: "service",
          rules: titleValidation('', {
            required: true
          }),
          render: renderProps => /*#__PURE__*/React.createElement(Autocomplete, {
            selectOnFocus: true,
            freeSolo: true,
            size: "small",
            "data-testid": "add-charge-service-selector",
            disabled: disableFields,
            inputValue: renderProps.field.value,
            textFieldProps: {
              inputRef: renderProps.field.ref,
              error: genericConnector(renderProps).showErrors
            },
            loading: loadingServiceTemplates,
            options: serviceTemplateOptions,
            getOptionLabel: getOptionLabel,
            getOptionSelected: getOptionSelected,
            onBlur: renderProps.field.onBlur,
            onInputChange: (_, value, reason) => {
              renderProps.field.onChange({
                target: {
                  value: value || ''
                }
              });
              if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
              const option = serviceTemplateOptions.find(s => s.label === value);
              applyServiceOptionChange(option || null, {
                value: '',
                label: value
              });
            },
            onChange: (event, value) => {
              const newValue = value ? value : null;
              applyServiceOptionChange(newValue);
            }
          })
        })
      }, {
        value: /*#__PURE__*/React.createElement(Controller, {
          control: control,
          name: "quantity",
          rules: {
            validate: validateGreaterThan0
          },
          render: renderProps => /*#__PURE__*/React.createElement(TextField, _extends({
            isMoney: true,
            size: "small",
            "data-testid": "add-charge-quantity",
            inputProps: {
              min: 0
            },
            disabled: !selectedServiceOption
          }, connectControllerToTextField(renderProps, {
            noHelperText: true
          })))
        })
      }, {
        value: /*#__PURE__*/React.createElement(Controller, {
          control: control,
          name: "rate",
          rules: {
            validate: value => value && (total == null || total.greaterThanOrEqualTo(0))
          },
          render: renderProps => /*#__PURE__*/React.createElement("div", {
            className: styles.rate
          }, /*#__PURE__*/React.createElement(TextField, _extends({
            isMoney: true,
            size: "small",
            "data-testid": "add-charge-rate",
            inputProps: {
              min: 0
            },
            disabled: !selectedServiceOption
          }, connectControllerToTextField(renderProps, {
            noHelperText: true
          }))), selectedService && serviceTemplateModel.isVariablePrice(selectedService.cost) && /*#__PURE__*/React.createElement(Typography, {
            ellipsis: true,
            className: styles.units,
            variant: "paragraph2",
            skin: "secondary",
            "data-testid": "add-charge-units"
          }, "/", getUnitName(serviceTemplateModel.unitName(selectedService))))
        })
      }, {
        value: /*#__PURE__*/React.createElement("div", {
          className: styles.discount
        }, /*#__PURE__*/React.createElement(DiscountTextField, {
          size: "small",
          "data-testid": "add-charge-discount",
          disabled: !selectedServiceOption,
          value: discount,
          onChange: _ref3 => {
            let {
              type,
              amount
            } = _ref3;
            const didTypeChange = type !== discount.type;
            if (didTypeChange) {
              setDiscount({
                type,
                amount: ''
              });
            } else {
              setDiscount({
                type,
                amount
              });
            }
          }
        }))
      }, {
        value: total != null ? /*#__PURE__*/React.createElement(Typography, {
          "data-testid": "add-charge-total",
          skin: total.lessThan(0) ? 'error' : 'standard'
        }, utils.formatDollarAmount(total)) : null
      }]
    }))
  });
}